import { z } from 'zod';

import { UuidSchema } from '../common/id/id';
import {
  AnswerValueSchema,
  type AnswerValue,
} from '../questions/answer-value-schema/answer-value-schema';
import { StableKeySchema } from '../questions/stable-key/stable-key';

/**
 * One stored answer, as its **owner** reads it back.
 *
 * Addressed by `questionKey`: the key is stable across every edit to the
 * question's wording (`stable-key.ts`), so a client that cached a wizard page
 * still matches its answers to their questions after staff rewrote the labels.
 * The `questionId` travels too, for clients that already hold the composed form
 * and would otherwise index by key twice.
 */
export const ProfileAnswerSchema = z
  .object({
    questionId: UuidSchema,
    questionKey: StableKeySchema,
    value: AnswerValueSchema,
    answeredAt: z.string(),
  })
  .strict();

export type ProfileAnswer = z.infer<typeof ProfileAnswerSchema>;

/**
 * One answer in a submission.
 *
 * `value` is **`unknown` at this boundary** and stays that way through the wire
 * schema (U4's untyped-boundary carve-out) — it cannot be validated here,
 * because what a valid value *is* depends on the question this key names, its
 * staff-authored validation blob and its own option rows. The api narrows it
 * per question with `answerValueSchema` (the one validator, U3); a schema that
 * pretended to check it here would be a second, weaker validator that always
 * disagreed with the real one.
 */
export const SubmitAnswerSchema = z
  .object({
    questionKey: StableKeySchema,
    value: z.unknown(),
  })
  .strict();

export type SubmitAnswer = z.infer<typeof SubmitAnswerSchema>;

/**
 * A batch of answers — one wizard page, or a whole registration step.
 *
 * Batched deliberately: the whole submission is validated and written in **one
 * transaction**, so a page can never land half-saved, and completion is
 * recomputed once rather than per field.
 *
 * A submission is a **partial** update — it carries the questions the member
 * just answered, never the whole profile — so omitting a key leaves its stored
 * answer alone. Clearing an answer is `value: null` on an optional question,
 * which is a different act from not mentioning it.
 */
export const SubmitAnswersSchema = z
  .object({
    answers: z.array(SubmitAnswerSchema).min(1).max(200),
  })
  .strict();

export type SubmitAnswersRequest = z.infer<typeof SubmitAnswersSchema>;

/**
 * Why one answer in a submission was refused — returned as a **list**, so a
 * member who mistyped three fields is told about all three rather than
 * discovering them one request at a time.
 */
export const AnswerRejectionSchema = z
  .object({
    questionKey: z.string(),
    reason: z.enum([
      /** No active question owns this key — a stale client, or a retired question. */
      'unknown_question',
      /** The question is not asked of this member's gender (`isInAudience`). */
      'out_of_audience',
      /** The question exists but is deactivated; its answers are kept, not extended. */
      'inactive_question',
      /** The value failed the question's own schema (`answerValueSchema`). */
      'invalid_value',
    ]),
    message: z.string(),
  })
  .strict();

export type AnswerRejection = z.infer<typeof AnswerRejectionSchema>;

/** The value type an answer carries, re-exported so consumers need one import. */
export type { AnswerValue };
